import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { Component, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { AuthService } from 'app/main/auth/auth.service';
import { SecurityService } from 'app/main/security/security.service';
import { fuseConfig } from 'app/fuse-config';
import { getDeviceId } from 'app/shared/device-id';
import * as jwt_decode from 'jwt-decode';
import { locale as english } from './i18n/en';
import { locale as spanish } from './i18n/es';
import { VideocallPipService } from './videocall-pip.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { WebrtcClientComponent } from './webrtc-client/webrtc-client.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

const notificationTypes = {
  start: 'start_videocall',
  cancel: 'cancel_videocall',
  answered: 'answered_videocall',
};

@Component({
    selector: 'app-videocall-pip',
    templateUrl: './videocall-pip.component.html',
    styleUrls: ['./videocall-pip.component.scss'],
    animations: [
        trigger('appear', [
            state('void', style({ opacity: 0, transform: 'scale(0.8)' })),
            transition(':enter', [
                animate('100ms ease', style({ opacity: 1, transform: 'scale(1)' })),
            ]),
        ]),
        trigger('visibilityChanged', [
            state('hidden', style({ opacity: 0, visibility: 'hidden', transform: 'scale(0.8)' })),
            state('visible', style({ opacity: 1, visibility: 'visible', transform: 'scale(1)' })),
            transition('visible => hidden', animate('100ms ease')),
        ]),
    ],
    standalone: true,
    imports: [NgIf, CdkDrag, MatButtonModule, MatIconModule, WebrtcClientComponent, TranslateModule, MatSnackBarModule]
})
export class VideocallPipComponent implements OnInit, OnDestroy {
  gateId: string | null = null;
  incomingCall = false;
  acceptedCall = false;
  wsClient: any;
  sessionData = {
    apiKey: '',
    sessionId: '',
    token: '',
    kiosk: '',
    callId: '',
    callerName: '',
  };
  hasAudio = true;
  hasVideo = true;
  @ViewChild(CdkDrag) videoCallContainer: CdkDrag;
  audio = new Audio('assets/sounds/ringtone-tizzy.wav');

  // Stable per-browser device id, used for initiator (auto-accept) matching.
  private deviceId: string | null = getDeviceId();
  // Ring timeout handle driven by expiresAt from the start payload.
  private ringTimeout: any = null;
  // CONCURRENT kiosks: the ringing UI is single-slot, so a second kiosk's start used to
  // OVERWRITE the first (last-write-wins — the first became unanswerable from the web).
  // Extra concurrent starts are queued here (raw payloads) and promoted one at a time when
  // the current ring clears (declined / answered elsewhere / cancelled / timed out), as long
  // as their own ring window hasn't expired.
  private pendingIncoming: any[] = [];

  constructor(
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    private securityService: SecurityService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private videocallService: VideocallPipService,
    private _translateService: TranslateService,
    private _ngZone: NgZone,
  ) {
    this._fuseTranslationLoaderService.loadTranslations(english, spanish);
  }

  ngOnInit() {
    // Subscribe to the gate ID changes
    this.securityService.subscribe((data: { value: string }) => {
      if (!data) {
        return;
      }

      this.gateId = data.value;

      // Start WebSocket when the gate ID is set
      this.setupVideocallWebSocket(data.value);
    });

    // Listen for logout event
    this.authService.subscribe(() => {
      this.handleLogout();
    });
  }

  /**
   * Releases the kiosk lock and tears down any pending/active call when the
   * tab is closed/reloaded. beforeunload runs synchronously so we fire the
   * cancel best-effort and clear the UI state.
   */
  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.releaseActiveCall(true);
  }

  ngOnDestroy() {
    this.releaseActiveCall();
    this.clearRingTimeout();
  }

  handleLogout() {
    this.releaseActiveCall();
    this.gateId = null;
    this.incomingCall = false;
    this.pendingIncoming = [];
    this.currentRingPayload = null;
    this.clearRingTimeout();
    if (this.wsClient) {
      this.wsClient.disconnect();
    }

    this.videocallService.setCallId(null);
    this.endCall();
  }

  /**
   * Posts a cancel to the kiosk so the server-side arbitration lock is freed
   * whenever we are leaving an incoming/accepted call (logout, tab close, end
   * call). callId-scoped; absent callId falls back to the per-kiosk cancel.
   * No-op when there is no kiosk in flight.
   */
  private releaseActiveCall(unloading = false) {
    const kiosk = this.sessionData?.kiosk;
    if (!kiosk) {
      return;
    }
    if (!this.incomingCall && !this.acceptedCall) {
      return;
    }
    if (unloading) {
      // beforeunload ABORTS in-flight XHR, so the Angular HttpClient cancel never reached
      // the API and the kiosk stayed "busy" until the Redis TTL. fetch(keepalive) survives
      // page unload AND supports the Authorization header (sendBeacon does not).
      try {
        const token = localStorage.getItem('token');
        fetch(`${fuseConfig.url}/videocall/virtualGuard/cancel/${kiosk}`, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: token } : {}),
            ...(this.deviceId ? { 'X-Device-Id': this.deviceId } : {}),
          },
          body: JSON.stringify(this.sessionData.callId ? { callId: this.sessionData.callId } : {}),
        }).catch(() => undefined);
      } catch (_) {
        /* best-effort */
      }
      return;
    }
    this.securityService
      .cancelVideoCall(kiosk, this.sessionData.callId)
      .subscribe({ next: () => {}, error: () => {} });
  }

  /**
   * Sets up a WebSocket connection for a video call with the specified gate ID.
   * @param gateId The ID of the gate to connect to.
   */
  setupVideocallWebSocket(gateId: string) {
    if (this.wsClient) {
      this.wsClient.disconnect();
    }

    this.wsClient = this.securityService.setupVideocallWebSocket();

    this.wsClient.subscribe(`/virtualGuardVideocall/${gateId}`, (data) => {
      // Keep the pending queue coherent even while in a call (the guards below short-circuit
      // everything during acceptedCall): a queued concurrent call that gets answered elsewhere
      // or cancelled must never be promoted later.
      if (
        (data.type === notificationTypes.cancel || data.type === notificationTypes.answered) &&
        data.callId
      ) {
        this.removePendingIncoming(data.callId);
      }

      if (
        this.acceptedCall ||
        !this.securityService.isCheckedIn ||
        !this.securityService.selectedBuildingSettings.showVirtualGuard
      ) {
        return;
      }

      if (data.type === notificationTypes.cancel || data.type === notificationTypes.answered) {
        // Dismiss-by-callId: only dismiss when the broadcast matches the call
        // we are ringing on. Fallback to current behavior when callId absent
        // on either side (old API / old kiosk).
        if (data.callId && this.sessionData.callId && data.callId !== this.sessionData.callId) {
          return;
        }
        this.dismissCall();
        return;
      }

      if (data.type !== notificationTypes.start) {
        return;
      }

      // callId dedup: ignore a duplicate start for the call we are already
      // ringing on so the auto-accept can't double-fire (M10). (Accepted calls
      // are already short-circuited by the guard above.)
      if (data.callId && this.incomingCall && data.callId === this.sessionData.callId) {
        return;
      }

      // CONCURRENT kiosk while another is already ringing:
      //  - our own outgoing call's fill-in (auto-accept initiator match) takes priority —
      //    the current ring goes to the queue and we answer ours;
      //  - any other concurrent start is QUEUED instead of overwriting the current ring
      //    (last-write-wins made the first call unanswerable from the web).
      if (this.incomingCall && data.callId && data.callId !== this.sessionData.callId) {
        if (!this.shouldAutoAccept(data)) {
          this.queuePendingIncoming(data);
          return;
        }
        this.queuePendingIncoming({ ...this.currentRingAsPayload() });
      }

      this.ringWithPayload(data);

      // Auto-accept only on the device that initiated the kiosk call (C6).
      // The broadcast initiator is a device_id (new API) or a userId (old API);
      // match against our local device_id, falling back to the legacy
      // accept_call flag when no initiator is provided.
      if (this.shouldAutoAccept(data)) {
        this.acceptCall();
      }
    });

    // Keep the web's checked-in state in sync with any other session of this same
    // guard (e.g. the mobile app), mirroring how the RN app reacts to the
    // `guard_state_changed` push. checked_in is per-user, so web + mobile share it.
    this.subscribeGuardState();
  }

  /**
   * Subscribes to the per-user guard-state channel so the web reflects a
   * check-in/check-out performed from ANY session of this guard. The browser has
   * no FCM, so the API broadcasts the same `guard_state_changed` event over Faye.
   */
  private subscribeGuardState() {
    const userId = this.getCurrentUserId();
    if (!userId || !this.wsClient) {
      return;
    }
    this.wsClient.subscribe(`/guardState/${userId}`, (data) => {
      if (!data || data.type !== 'guard_state_changed') {
        return;
      }
      const checkedIn = data.checked_in === '1' || data.checked_in === true;
      // Faye callbacks run outside Angular's zone — re-enter so the toolbar's
      // check-in/out button and the call-receiving gate reflect it immediately.
      this._ngZone.run(() => {
        this.securityService.isCheckedIn = checkedIn;
      });
    });
  }

  /**
   * Resolves the current guard's user id from the JWT (the security token carries
   * it as the audience / jwtid claim). Returns null when unavailable.
   */
  private getCurrentUserId(): string | null {
    const token = this.securityService.authToken;
    if (!token) {
      return null;
    }
    try {
      const decoded: any = jwt_decode.default(token);
      const aud = decoded?.aud;
      return (Array.isArray(aud) ? aud[0] : aud) || decoded?.jti || null;
    } catch {
      return null;
    }
  }

  /**
   * Decide whether this client should auto-accept the incoming start_videocall.
   * New API: broadcast `initiator` is the initiating device_id => only the
   * matching device auto-accepts. Old API: no initiator, fall back to the
   * legacy `accept_call` flag.
   */
  private shouldAutoAccept(data: any): boolean {
    if (!data?.accept_call) {
      return false;
    }
    if (data.initiator) {
      return !!this.deviceId && data.initiator === this.deviceId;
    }
    // Old API without initiator: preserve legacy accept_call behavior.
    return true;
  }

  // Raw start payload of the CURRENT ring (needed to re-queue it when our own outgoing
  // fill-in takes priority, and to know its expiresAt).
  private currentRingPayload: any = null;

  /** Ring the UI with a start payload: session data (incl. caller name), sound, ring TTL. */
  private ringWithPayload(data: any) {
    this.currentRingPayload = data;
    this.incomingCall = true;
    this.playCallSound();
    this.sessionData = {
      apiKey: data.apiKey,
      sessionId: data.sessionId,
      token: data.guardUserToken,
      kiosk: data.kiosk,
      callId: data.callId,
      callerName: data.callerName || '',
    };
    // Ring timeout driven by expiresAt (epoch, sec or ms). Falls back to no
    // explicit timeout when absent (old API), preserving current behavior.
    this.scheduleRingTimeout(data.expiresAt);
  }

  private currentRingAsPayload(): any {
    return this.currentRingPayload || {};
  }

  /** Queue a concurrent start payload (dedup by callId, only future-dated ring windows). */
  private queuePendingIncoming(data: any) {
    if (!data?.callId || this.remainingRingMs(data.expiresAt) === null) {
      return;
    }
    this.removePendingIncoming(data.callId);
    this.pendingIncoming.push(data);
  }

  private removePendingIncoming(callId: string) {
    this.pendingIncoming = this.pendingIncoming.filter((p) => p?.callId !== callId);
  }

  /**
   * Promote the next queued concurrent call whose ring window is still open.
   * Called whenever the current ring clears WITHOUT being answered here.
   */
  private tryPromotePending() {
    while (this.pendingIncoming.length > 0) {
      const next = this.pendingIncoming.shift();
      if (this.remainingRingMs(next?.expiresAt) !== null) {
        this.ringWithPayload(next);
        return;
      }
    }
  }

  /**
   * Remaining ms of a ring window given expiresAt (epoch sec or ms). Returns null when
   * expired/unparseable — treat as not ringable. Absent expiresAt (old API) => null too,
   * queued entries need a real window to be promoted safely later.
   */
  private remainingRingMs(expiresAt: any): number | null {
    const raw = Number(expiresAt);
    if (!Number.isFinite(raw) || raw <= 0) {
      return null;
    }
    const expiresMs = raw < 1e12 ? raw * 1000 : raw;
    const remaining = expiresMs - Date.now();
    return remaining > 0 ? remaining : null;
  }

  /**
   * Schedules a ring timeout based on the server-authoritative expiresAt.
   * Normalizes seconds-vs-milliseconds like the RN clients.
   */
  private scheduleRingTimeout(expiresAt: any) {
    this.clearRingTimeout();
    if (expiresAt === undefined || expiresAt === null || expiresAt === '') {
      return;
    }
    const raw = Number(expiresAt);
    if (!Number.isFinite(raw) || raw <= 0) {
      return;
    }
    const expiresMs = raw < 1e12 ? raw * 1000 : raw;
    const delay = expiresMs - Date.now();
    if (delay <= 0) {
      // Already expired => dismiss immediately.
      this.dismissCall();
      return;
    }
    this.ringTimeout = setTimeout(() => {
      this.ringTimeout = null;
      if (this.incomingCall && !this.acceptedCall) {
        this.dismissCall();
      }
    }, delay);
  }

  private clearRingTimeout() {
    if (this.ringTimeout) {
      clearTimeout(this.ringTimeout);
      this.ringTimeout = null;
    }
  }

  acceptCall() {
    this.incomingCall = false;
    this.acceptedCall = true;
    this.clearRingTimeout();

    this.stopCallSound();

    this.securityService.answerVideoCall(this.gateId, this.sessionData.callId).subscribe({
      next: () => { },
      error: (err) => {
        // 409 dead-answer (ring expired / this guard busy elsewhere) or network failure:
        // the backend never registered this answer, so staying in the call view would trap
        // the guard in a call the server doesn't know about. Surface it and dismiss WITHOUT
        // posting a cancel — the call may legitimately belong to another guard by now.
        const message = err?.error?.message || 'Could not answer the call';
        this.snackBar.open(message, 'OK', { duration: 5000, panelClass: ['snack-error'] });
        this.videocallService.setCallId(null);
        this.dismissCall();
      },
    });

    this.videocallService.setCallId(this.sessionData.callId);
  }

  /**
   * User-initiated reject of an incoming (unanswered) ring. Per the C4 decision
   * ("declinar solo silencia ese dispositivo"), a decline must NOT cancel the
   * kiosk call nor dismiss sibling sessions — it only silences THIS device, so
   * other guards on the gate can still answer and the kiosk's own no-answer
   * timeout / another guard's answer governs the call lifecycle. The kiosk lock
   * is freed by answer / kiosk-cancel / ring TTL / release-on-RTC-end, not here.
   */
  hideCall() {
    this.dismissCall();
  }

  /**
   * Clears the incoming/ringing UI WITHOUT posting a cancel. Used for remote
   * dismissals (cancel_videocall / answered_videocall) and after a cancel has
   * already been posted by hideCall()/release. If other kiosks queued up while
   * this one was ringing, the next valid one starts ringing immediately.
   */
  private dismissCall() {
    this.incomingCall = false;
    this.acceptedCall = false;
    this.currentRingPayload = null;
    this.clearRingTimeout();

    this.resetVideocallPosition();
    this.stopCallSound();

    this.tryPromotePending();
  }


  handleError(error: any) {
    if (error) {
      this.snackBar.open(error.message, 'OK', {
        duration: 5000,
        panelClass: ["snack-error"],
      });
    }
  }

  /**
   * Ends the ongoing video call session and cleans up resources.
   */
  endCall() {
    this.acceptedCall = false;

    this.hasAudio = true;
    this.hasVideo = true;
    this.resetVideocallPosition();
    this.stopCallSound();
  }

  /**
   * Resets the position of the video call container after a delay of 250ms.
   */
  resetVideocallPosition() {
    setTimeout(() => {
      if (this.videoCallContainer) {
        this.videoCallContainer.reset();
      }
    }, 250);
  }

  playCallSound() {
    this.audio.loop = true;
    this.audio.play();
  }

  stopCallSound() {
    this.audio.pause();
  }

  handleEndCall(reason?: string) {
    // Two guards raced to answer the same call and WE lost (someone else is already in the
    // RTC room): bail locally WITHOUT posting a cancel — releasing here would send
    // cancel_videocall to the kiosk and kill the call the winner just joined.
    if (reason !== 'session_already_exists') {
      // Release the kiosk lock for the call that just ended, then tear down.
      this.releaseActiveCall();
    }
    this.videocallService.setCallId(null);
    this.endCall();
  }

  handleSingleClickOpenDoor() {
    this.snackBar.open('You must double click to open the door', 'OK', {
      duration: 5000,
    });
  }
}
